import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@heroui/react';
import { useTranslation } from '@/i18n/hooks/useTranslation';

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  buttonText?: string;
  leadType?: string;
  customMessage?: string;
}

/**
 * Modal de éxito reutilizable para diferentes tipos de formularios
 */
const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  title: titleProp,
  message: messageProp,
  buttonText: buttonTextProp,
  leadType,
  customMessage,
}) => {
  const { t } = useTranslation();
  // Mismos defaults de siempre (sólo cuando la prop no viene), ahora traducibles.
  const title =
    titleProp === undefined ? t('formsUi.success.title') : titleProp;
  const message =
    messageProp === undefined ? t('formsUi.success.message') : messageProp;
  const buttonText =
    buttonTextProp === undefined ? t('formsUi.success.button') : buttonTextProp;

  // Mensajes personalizados según el tipo de lead
  const getCustomMessage = () => {
    // Si hay un mensaje personalizado, usarlo
    if (customMessage) return customMessage;

    if (!leadType) return message;

    switch (leadType) {
      case 'buy-consignment':
        return t('formsUi.success.consignment');
      case 'buy-direct':
        return t('formsUi.success.buyDirect');
      case 'sell-financing':
        return t('formsUi.success.financing');
      case 'contact-general':
        return t('formsUi.success.contact');
      case 'search-request':
        return t('formsUi.success.search');
      default:
        return message;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      placement='center'
      classNames={{
        body: 'py-6',
        backdrop: 'bg-[#292f46]/50 backdrop-opacity-40',
        base: 'border-[#292f46] bg-white dark:bg-gray-900',
        header: 'border-b-[1px] border-[#292f46]',
        footer: 'border-t-[1px] border-[#292f46]',
      }}
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3'>
              <svg
                className='h-6 w-6 text-green-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-white'>
              {title}
            </h3>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className='text-center'>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              {getCustomMessage()}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color='primary'
            onPress={onClose}
            className='w-full bg-primary text-white'
          >
            {buttonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SuccessModal;
